import React, { useState } from "react";
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ProductionQuantityLimitsOutlinedIcon from '@mui/icons-material/ProductionQuantityLimitsOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import PrintIcon from '@mui/icons-material/Print';
import InfoIcon from '@mui/icons-material/Info';
import { Tooltip } from "@mui/material";
import Carrier from '../../pages/General/Carrier';
import ShipmentRate from '../../pages/General/ShipmentRate';
import { getCarrierDisplayName } from '../General/carrierMapper';

// Professional, streamlined Accordion configurations
const Accordion = styled((props) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: theme.spacing(1.5),
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    '&:not(:last-child)': {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '&:before': {
        display: 'none',
    },
}));

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.8rem', color: '#637381' }} />}
        {...props}
    />
))(({ theme }) => ({
    backgroundColor: '#fff',
    flexDirection: 'row-reverse',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    borderBottom: '1px solid transparent',
    '&.Mui-expanded': {
        borderBottom: '1px solid #f4f6f8',
        backgroundColor: '#fafbfc'
    },
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
        marginLeft: theme.spacing(1.5),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%'
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(3),
    backgroundColor: '#fff',
}));

// Premium dashboard row layout helper
const InfoRow = ({ label, value, tooltip = null }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.25 }}>
        <Typography variant="body2" sx={{ color: '#637381', fontWeight: 500 }}>
            {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" sx={{ color: '#212b36', fontWeight: 600 }}>
                {value}
            </Typography>
            {tooltip && (
                <Tooltip title={tooltip} placement="top">
                    <InfoIcon sx={{ color: '#919eab', fontSize: 16, cursor: 'help' }} />
                </Tooltip>
            )}
        </Box>
    </Box>
);

// Helper function to dynamically generate a clean string for duty breakdowns
const renderDutyTooltip = (dutyBreakdown) => {
    if (!dutyBreakdown || typeof dutyBreakdown !== 'object') return null;

    const elements = [];
    const actualBreakdown = Array.isArray(dutyBreakdown) ? dutyBreakdown[0] : dutyBreakdown;

    Object.entries(actualBreakdown).forEach(([key, val]) => {
        if (val === undefined || val === null || val === "") return;

        // Strip suffixes like "_3"
        const cleanKey = key.replace(/_\d+$/, ''); 
        
        let formattedKey = cleanKey
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.toLowerCase().slice(1))
            .join(' ');

        // --- RENAME REQUIREMENT: Map Zonos Markup directly to SC Fee ---
        if (formattedKey === "Zonos Markup") {
            formattedKey = "SC Fee";
        }

        if (typeof val === 'object' && !Array.isArray(val)) {
            const resolvedValue = val.value !== undefined ? val.value : (val.amount !== undefined ? val.amount : val.price);
            if (resolvedValue !== undefined && resolvedValue !== null && resolvedValue !== "") {
                const currencyStr = val.currency ? ` ${val.currency}` : "";
                elements.push(`${formattedKey}: ${resolvedValue}${currencyStr}`);
            }
        } else {
            elements.push(`${formattedKey}: ${val}`);
        }
    });

    if (elements.length === 0) return null;

    return (
        <Box sx={{ p: 0.5 }}>
            {elements.map((text, idx) => (
                <Typography 
                    key={idx} 
                    variant="caption" 
                    display="block" 
                    sx={{ 
                        fontSize: '11px', 
                        color: '#ffffff',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {text}
                </Typography>
            ))}
        </Box>
    );
};

const ShipmentAccordion = ({ shipment = null, setPrintUrl, setIsPrinted }) => {
    const profile = useSelector(state => state.auth.profile);
    const [expanded, setExpanded] = useState("");

    const handleChange = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
    };

    const labelButton = (row) => {
        let labelButtonDisable = true;
        if (row &&
            row.label &&
            row.manifest &&
            typeof row.label.is_void_in_progress === "undefined" &&
            (row.label.url_detachable || row.label.url_direct_detachable || row.label.url || row.label.url_direct)
        ) {
            labelButtonDisable = false;
        }
        return labelButtonDisable;
    };

    if (!shipment || !shipment.shipment_details) return null;

    return (
        Object.keys(shipment.shipment_details).map(function (key) {
            const details = shipment.shipment_details[key];
            if (!details) return null;

            const resolvedRateObj = details.shipment_rates || details.rate;
            const billedData = resolvedRateObj?.billed;
            const breakdownData = resolvedRateObj?.breakdown;

            const multiPieceFlag = details.label?.multi_piece || details.is_multi_piece_shipment;
            const voidedFlag = labelButton(details);

            let url = "";
            if (details.label) {
                url = details.label.url_detachable || 
                      details.label.url_direct_detachable || 
                      details.label.url || 
                      details.label.url_direct || "";
            }

            const dutyTooltipContent = renderDutyTooltip(breakdownData?.duty_breakdown);
            
            // --- HIDE ROW REQUIREMENT: Determine if the Duties row should render ---
            const hasDuties = breakdownData?.duty_charge !== undefined && 
                              breakdownData?.duty_charge !== null && 
                              parseFloat(breakdownData.duty_charge) > 0;

            return (
                <div key={key}>
                    <Accordion expanded={expanded === `panel${key}`} onChange={handleChange(`panel${key}`)}>
                        <AccordionSummary aria-controls={`panel${key}d-content`} id={`panel${key}d-header`}>
                            {/* Left Summary Header Details */}
                            <Box sx={{ display: 'flex', alignItems: "center", gap: 2 }}>
                                {(profile?.hide_carrier === false && details.label?.carrier_name) && (
                                    <Carrier 
                                        carrierName={getCarrierDisplayName(
                                            details.label.carrier_name,
                                            details.tracking_number
                                        )} 
                                        width="90px"
                                    />
                                )}
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#161c24', letterSpacing: '0.3px' }}>
                                    {details.tracking_number ? details.tracking_number : "-"}
                                </Typography>
                                {details.shipment_status && (
                                    <Chip 
                                        label={details.shipment_status} 
                                        size="small"
                                        sx={{ 
                                            backgroundColor: '#e6f4ea', 
                                            color: '#1e7e34', 
                                            fontWeight: 700,
                                            fontSize: '11px' 
                                        }} 
                                    />
                                )}
                            </Box>

                            {/* Right Action Control Buttons */}
                            <Box sx={{ display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                                {url && (
                                    <>
                                        <Button 
                                            size="small"
                                            disabled={voidedFlag} 
                                            variant="outlined" 
                                            startIcon={<RemoveRedEyeOutlinedIcon />}
                                            onClick={() => window.open(url, '_blank')}
                                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '6px' }}
                                        >
                                            View
                                        </Button>
                                        <Button 
                                            size="small"
                                            disabled={voidedFlag} 
                                            variant="contained" 
                                            disableElevation
                                            startIcon={<PrintIcon />}
                                            onClick={() => { setPrintUrl(url); setIsPrinted(false); }}
                                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '6px', bgcolor: '#212b36', '&:hover': { bgcolor: '#161c24' } }}
                                        >
                                            Print
                                        </Button>
                                    </>
                                )}
                            </Box>
                        </AccordionSummary>
                        
                        <AccordionDetails>
                            <Grid container spacing={4}>
                                {/* Block 1: Shipment Info */}
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <LocalShippingOutlinedIcon sx={{ color: '#637381', fontSize: 20 }} />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#212b36', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px' }}>
                                            Shipment Details
                                        </Typography>
                                    </Box>
                                    <Box sx={{ bgcolor: '#f8f9fa', p: 2, borderRadius: '8px' }}>
                                        <InfoRow label="Service Type" value={details.label?.service_type || "-"} />
                                        <Divider sx={{ borderStyle: 'dashed' }} />
                                        <InfoRow label="Shipped Date" value={details.shipped_date || "-"} />
                                        <Divider sx={{ borderStyle: 'dashed' }} />
                                        <InfoRow label="Status" value={details.shipment_status || "-"} />
                                        <Divider sx={{ borderStyle: 'dashed' }} />
                                        <InfoRow label="Delivered Date" value={details.delivered_date || "-"} />
                                    </Box>
                                </Grid>

                                {/* Block 2: Package Specifications */}
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <ProductionQuantityLimitsOutlinedIcon sx={{ color: '#637381', fontSize: 20 }} />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#212b36', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px' }}>
                                            Package Specifications
                                        </Typography>
                                    </Box>
                                    <Box sx={{ bgcolor: '#f8f9fa', p: 2, borderRadius: '8px' }}>
                                        <InfoRow label="Quantity" value={details.product_info?.quantity || "-"} />
                                        <Divider sx={{ borderStyle: 'dashed' }} />
                                        <InfoRow 
                                            label="Weight" 
                                            value={details.product_info?.actual_weight ? (details.product_info.weight_unit ? `${details.product_info.actual_weight} ${details.product_info.weight_unit}` : details.product_info.actual_weight) : "-"} 
                                        />
                                        <Divider sx={{ borderStyle: 'dashed' }} />
                                        <InfoRow 
                                            label="Dimensions" 
                                            value={details.product_info?.dimensions ? `${details.product_info.dimensions.length || ""}${details.product_info.dimensions.length_unit || ""} × ${details.product_info.dimensions.width || ""}${details.product_info.dimensions.width_unit || ""} × ${details.product_info.dimensions.height || ""}${details.product_info.dimensions.height_unit || ""}` : "-"} 
                                        />
                                        <Divider sx={{ borderStyle: 'dashed' }} />
                                        <InfoRow 
                                            label="Insurance Value" 
                                            value={details.product_info?.insurance_amount || "-"} 
                                            tooltip={details.product_info?.insurance_amount ? "Commercial invoice or insured value whichever is lower" : null}
                                        />
                                    </Box>
                                </Grid>

                                {/* Block 3: Billing Info */}
                                {(profile?.hide_rate === false || typeof profile?.hide_rate === "undefined") && (
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <AccountBalanceWalletOutlinedIcon sx={{ color: '#637381', fontSize: 20 }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#212b36', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px' }}>
                                                Charges & Financials
                                            </Typography>
                                        </Box>
                                        <Box sx={{ bgcolor: '#f8f9fa', p: 2, borderRadius: '8px' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.25 }}>
                                                <Typography variant="body2" sx={{ color: '#637381', fontWeight: 500 }}>Base Cost</Typography>
                                                <ShipmentRate billed_rate={billedData?.base_cost} api_rate={breakdownData?.sub_total} is_multi_piece={multiPieceFlag} is_voided={voidedFlag} />
                                            </Box>
                                            <Divider sx={{ borderStyle: 'dashed' }} />

                                            {/* CONDITIONAL DUTIES ROW RENDERING */}
                                            {hasDuties && (
                                                <>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.25 }}>
                                                        <Typography variant="body2" sx={{ color: '#637381', fontWeight: 500 }}>Duties</Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography variant="body2" sx={{ color: '#212b36', fontWeight: 600 }}>
                                                                {breakdownData.duty_charge}
                                                            </Typography>
                                                            {dutyTooltipContent && (
                                                                <Tooltip title={dutyTooltipContent} placement="top" arrow>
                                                                    <InfoIcon sx={{ color: '#919eab', fontSize: 16, cursor: 'help' }} />
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                    <Divider sx={{ borderStyle: 'dashed' }} />
                                                </>
                                            )}
                                            
                                            <InfoRow 
                                                label="Adjustment" 
                                                value={billedData?.adjustment_cost !== undefined && billedData?.adjustment_cost !== null ? billedData.adjustment_cost : "-"}
                                                tooltip={billedData?.adjustment_cost !== undefined && resolvedRateObj?.adjustment_description ? resolvedRateObj.adjustment_description : null}
                                            />
                                            <Divider sx={{ borderStyle: 'dashed' }} />
                                            
                                            <InfoRow 
                                                label="Tax" 
                                                value={billedData?.tax !== undefined && billedData?.tax !== null ? billedData.tax : "-"} 
                                            />
                                            <Divider sx={{ borderStyle: 'dashed' }} />
                                            
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.25 }}>
                                                <Typography variant="body2" sx={{ color: '#212b36', fontWeight: 700 }}>Total Cost</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    <ShipmentRate billed_rate={billedData?.total} api_rate={breakdownData?.sub_total || breakdownData?.total_charge} is_multi_piece={multiPieceFlag} is_voided={voidedFlag} />
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                </div>
            );
        })
    );
};

export default ShipmentAccordion;